import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { Messages } from '../../../../../../gen-src/Localize';
import {
  ColorPaletteName,
  createCountBarCellFormatter,
  createTextCellFormatter,
  PlaceholderTextCell,
  TypedBodyCellProps,
} from '../../../../data-catalog-shared';
import { LocationTypes } from '../../sharedDisplayGrid/types/LocationTypes';
import { createLinkCellFormatter, createLinkOrTextCellFormatter, createLocationLinkOrTextCellFormatter } from './LinkCellFormatter';
import { createNamesWithCountCellFormatter } from './NamesWithCountCell';

type obfuscatedCellProps = {
  readonly noPadding?: boolean
};

export const PermissionsRequiredCell: React.Factory<obfuscatedCellProps>
  = (props: obfuscatedCellProps) => <PlaceholderTextCell placeholderText={Messages.PermissionsRequired()} noPadding={props.noPadding} />;

export const EmptyCell: React.Factory<unknown> = () => <div />;

export function createObfuscatedLinkCellFormatter<T>(
  shouldBeObfuscated: (rowData: T) => boolean,
  getTextFromRowData: (rowData: T) => string | null,
  getPathFromRowData: (urls: SiteUrls, rowData: T) => string,
  noPadding = false,
  disableTabIndex = false,
  ObfuscatedComponent: React.Factory<obfuscatedCellProps> = PermissionsRequiredCell,
  getDisabledFromRowData?: (rowData: T) => boolean,
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => {
    return shouldBeObfuscated(props.row.data) ?
      <ObfuscatedComponent noPadding={noPadding} />
      : createLinkCellFormatter(
        getTextFromRowData,
        getPathFromRowData,
        noPadding,
        undefined, // useAnchor
        disableTabIndex,
        undefined, // getIconFromRowData
        undefined, // getLinkTitleFromRowData
        getDisabledFromRowData
      )(props);
  };
}

export function createObfuscatedLinkWithIconCellFormatter<T>(
  shouldBeObfuscated: (rowData: T) => boolean,
  getTextFromRowData: (rowData: T) => string | null,
  getPathFromRowData: (urls: SiteUrls, rowData: T) => string,
  noPadding = false,
  disableTabIndex = false,
  ObfuscatedComponent: React.Factory<obfuscatedCellProps> = PermissionsRequiredCell,
  getIconFromRowData: (rowData: T) => JSX.Element | null,
  getLinkTitleFromRowData: (rowData: T) => string | null,
  getDisabledFromRowData?: (rowData: T) => boolean,
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => {
    return shouldBeObfuscated(props.row.data)
      ? <ObfuscatedComponent noPadding={noPadding} />
      : createLinkCellFormatter(
        getTextFromRowData,
        getPathFromRowData,
        noPadding,
        false, // useAnchor
        disableTabIndex,
        getIconFromRowData,
        getLinkTitleFromRowData,
        getDisabledFromRowData
      )(props);
  };
}

export function createObfuscatedLinkOrTextCellFormatter<T>(
  shouldBeObfuscated: (rowData: T) => boolean,
  getTextFromRowData: (rowData: T) => string | null,
  getPathFromRowData: (urls: SiteUrls, rowData: T) => string | null,
  noPadding = false,
  targetBlank = false,
  useAnchor = false,
  ObfuscatedComponent: React.Factory<obfuscatedCellProps> = PermissionsRequiredCell,
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => {
    return shouldBeObfuscated(props.row.data) ?
      <ObfuscatedComponent noPadding={noPadding} />
      : createLinkOrTextCellFormatter(getTextFromRowData, getPathFromRowData, noPadding, targetBlank, useAnchor)(props);
  };
}

export function createObfuscatedLocationLinkOrTextCellFormatter<T>(
  shouldBeObfuscated: (rowData: T) => boolean,
  getTextFromRowData: (rowData: T) => string | null | LocationTypes,
  getPathFromRowData: (urls: SiteUrls, rowData: T) => string | null,
  noPadding = false,
  targetBlank = false,
  useAnchor = false,
  ObfuscatedComponent: React.Factory<obfuscatedCellProps> = PermissionsRequiredCell,
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => {
    return shouldBeObfuscated(props.row.data) ?
      <ObfuscatedComponent noPadding={noPadding} />
      : createLocationLinkOrTextCellFormatter(getTextFromRowData, getPathFromRowData, noPadding, targetBlank, useAnchor)(props);
  };
}

export function createObfuscatedTextCellFormatter<T>(
  shouldBeObfuscated: (rowData: T) => boolean,
  getTextFromRowData: (rowData: T) => string | null,
  placeholder?: string,
  getDisabledFromRowData?: (rowData: T) => boolean,
  ObfuscatedComponent: React.Factory<unknown> = EmptyCell
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => {
    return shouldBeObfuscated(props.row.data) ?
      <ObfuscatedComponent />
      : createTextCellFormatter(getTextFromRowData, placeholder, getDisabledFromRowData)(props);
  };
}

export function createObfuscatedTextWithIconCellFormatter<T>(
  shouldBeObfuscated: (rowData: T) => boolean,
  getTextFromRowData: (rowData: T) => string | null,
  getIconFromRowData: (rowData: T) => JSX.Element | null,
  getCellTitleFromRowData: (rowData: T) => string | null,
  placeholder?: string,
  getDisabledFromRowData?: (rowData: T) => boolean,
  ObfuscatedComponent: React.Factory<unknown> = EmptyCell
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => {
    return shouldBeObfuscated(props.row.data) ?
      <ObfuscatedComponent />
      : createTextCellFormatter(
        getTextFromRowData,
        placeholder,
        getDisabledFromRowData,
        false, // noPadding
        getIconFromRowData,
        getCellTitleFromRowData,
      )(props);
  };
}

export function createObfuscatedCountsCellFormatter<T>(
  shouldBeObfuscated: (rowData: T) => boolean,
  getCountFromRowData: (rowData: T) => number | null,
  getMaxFromRowData: (rowData: T) => number | null,
  colorPaletteName?: ColorPaletteName,
  getDisabledFromRowData?: (rowData: T) => boolean,
  ObfuscatedComponent: React.Factory<unknown> = EmptyCell
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => {
    return shouldBeObfuscated(props.row.data) ?
      <ObfuscatedComponent />
      : createCountBarCellFormatter(getCountFromRowData, getMaxFromRowData, colorPaletteName, getDisabledFromRowData)(props);
  };
}

export function createObfuscatedNamesWithCountCellFormatter<T>(
  shouldBeObfuscated: (rowData: T) => boolean,
  getNamesFromRowData: (rowData: T) => string[],
  getCountFromRowData: (rowData: T) => number,
  maxShown = 3,
  ObfuscatedComponent: React.Factory<unknown> = EmptyCell,
  getDisabledFromRowData?: (rowData: T) => boolean,
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => {
    return shouldBeObfuscated(props.row.data) ?
      <ObfuscatedComponent />
      : createNamesWithCountCellFormatter(getNamesFromRowData, getCountFromRowData, maxShown, getDisabledFromRowData)(props);
  };
}
